import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Shield } from 'lucide-react';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes - show amber
const STORAGE_KEY = 'lastActivityTime';

/**
 * Session Timer Component
 * Shows remaining session time in the header
 * Color-coded: Green (>5 min), Amber (2-5 min), Red (<2 min)
 */
export function SessionTimer({ enabled = true }) {
  const [remainingSeconds, setRemainingSeconds] = useState(SESSION_TIMEOUT_MS / 1000);
  const [isWarning, setIsWarning] = useState(false);
  const [isCritical, setIsCritical] = useState(false);

  const calculateRemaining = useCallback(() => {
    const lastActivity = localStorage.getItem(STORAGE_KEY);
    if (lastActivity) {
      const elapsed = Date.now() - parseInt(lastActivity, 10);
      const remaining = Math.max(0, SESSION_TIMEOUT_MS - elapsed);
      return Math.floor(remaining / 1000);
    }
    return SESSION_TIMEOUT_MS / 1000;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Update every second
    const interval = setInterval(() => {
      const seconds = calculateRemaining();
      setRemainingSeconds(seconds);
      setIsWarning(seconds <= WARNING_THRESHOLD_MS / 1000 && seconds > 120);
      setIsCritical(seconds <= 120);
    }, 1000);

    // Initial calculation
    setRemainingSeconds(calculateRemaining());

    return () => clearInterval(interval);
  }, [enabled, calculateRemaining]);

  if (!enabled) return null;

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  // Determine color based on remaining time
  let bgColor = 'bg-emerald-500/20';
  let textColor = 'text-emerald-400';
  let borderColor = 'border-emerald-500/30';
  let iconColor = 'text-emerald-400';

  if (isCritical) {
    bgColor = 'bg-red-500/20';
    textColor = 'text-red-400';
    borderColor = 'border-red-500/30';
    iconColor = 'text-red-400';
  } else if (isWarning) {
    bgColor = 'bg-amber-500/20';
    textColor = 'text-amber-400';
    borderColor = 'border-amber-500/30';
    iconColor = 'text-amber-400';
  }

  return (
    <div 
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${bgColor} border ${borderColor} transition-colors duration-300`}
      data-testid="session-timer"
      title="Session time remaining (CJIS compliance: auto-logout after 30 min inactivity)"
    >
      <div className="flex items-center gap-1">
        {isCritical ? (
          <Clock className={`w-3.5 h-3.5 ${iconColor} animate-pulse`} />
        ) : (
          <Shield className={`w-3.5 h-3.5 ${iconColor}`} />
        )}
      </div>
      <span 
        className={`text-xs font-mono font-medium ${textColor}`}
        data-testid="session-timer-value"
      >
        {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
      </span>
    </div>
  );
}

export default SessionTimer;
